import {
  blk_arc_interfaces,
  bosdyn_api_msgs,
  container_manager_interfaces,
  data_acquisition_interfaces,
  fluke_sensor_interfaces,
  localization_handler_interfaces,
  opg_msgs,
  signalr_client_interfaces,
  spot_localization_handler_interfaces,
  spot_msgs,
  spot_ncb_rms_actions_interfaces,
  spot_spotcam_ptz_interfaces,
  std_srvs,
  webrtc_streaming_interfaces,
} from 'rclnodejs';
import { useMemo } from 'react';
import { ValueOf } from 'type-fest';

import { useDeviceName } from 'lib/api/myRobot';
import { RosSignalR } from 'lib/roslibjs';

import type * as aerial_interfaces_srv from 'lib/ros-abstraction/aerial/aerial-interfaces-srv';
import type * as robot_interfaces_srv from 'lib/ros-abstraction/common/robot-interfaces-srv';

import { DeviceName } from 'types/models';

import { IRosTypeBosdynSpotApiMsgsLocomotionHintConst } from '../../types/generated/ros-msgs';
import { RequestStreamChangePayload } from './services';
import { RosActionName } from './use-ros-action';
import { ROS_API } from './use-ros-api';
import { useRosBridge } from './use-ros-bridge';

type Trigger_Request = std_srvs.srv.Trigger_Request;
type Trigger_Response = std_srvs.srv.Trigger_Response;

export const DEVICE_NAME_SERVICES = {
  NCB_AVAILABLE_SERVICES: (deviceName: DeviceName) =>
    `/${deviceName}/ncb/available_services` as const,
  RMS_AVAILABLE_SERVICES: (deviceName: DeviceName) =>
    `/${deviceName}/rms/available_services` as const,
  NCB_CALL_SERVICE: (deviceName: DeviceName) => `/${deviceName}/ncb/call_service` as const,
  LOCALIZE_TO_MAP: (deviceName: DeviceName) => `/${deviceName}/localize_to_map` as const,
  START_MAP_RECORDING: (deviceName: DeviceName) => `/${deviceName}/start_map_recording` as const,
  STOP_MAP_RECORDING: (deviceName: DeviceName) => `/${deviceName}/stop_map_recording` as const,
  CANCEL_MAP_RECORDING: (deviceName: DeviceName) => `/${deviceName}/cancel_map_recording` as const,
  REQUEST_STREAM_CHANGE: (deviceName: DeviceName) =>
    `/${deviceName}/handle_request_stream_change` as const,
  PLAY_SOUND_FROM_BLOB: (deviceName: DeviceName) =>
    `/${deviceName}/spotcam/play_sound_from_blob` as const,
  ROBOT_COMMAND: (deviceName: DeviceName) => `/${deviceName}/robot_command` as const,
  CLEAR_BEHAVIOR_FAULT: (deviceName: DeviceName) => `/${deviceName}/clear_behavior_fault` as const,
  SET_FLUKE_STREAM_FREQUENCY_RANGE: (deviceName: DeviceName) =>
    `/${deviceName}/set_fluke_stream_frequency_range` as const,
  CAPTURE_STORE_FLUKE_IMAGE: (deviceName: DeviceName) =>
    `/${deviceName}/capture_store_fluke_image` as const,
  RELEASE: (deviceName: DeviceName) => `/${deviceName}/release` as const,
  BLK_ARC_START_CAPTURE: (deviceName: DeviceName) =>
    `/${deviceName}/blk_arc/start_capture` as const,
  BLK_ARC_CANCEL_CAPTURE: (deviceName: DeviceName) =>
    `/${deviceName}/blk_arc/stop_capture_and_discard` as const,
  RESTART_ROBOT: (deviceName: DeviceName) => `/${deviceName}/restart_robot` as const,
  SPOT_CAM_PLAY_SOUND: (deviceName: DeviceName) =>
    `/${deviceName}/spotcam/play_sound_direct_method` as const,
  SPOT_CAM_SET_SURROUND_VIEW: (deviceName: DeviceName) =>
    `/${deviceName}/spotcam/set_surround_view` as const,
  SPOT_CAM_POINT_FROM_PANO_CLICK: (deviceName: DeviceName) =>
    `/${deviceName}/spotcam/point_from_pano_click` as const,
  SPOT_CAM_PTZ_RECENTER: (deviceName: DeviceName) => `/${deviceName}/spotcam/ptz/recenter` as const,
  SPOT_CAM_CAPTURE_IMAGE: (deviceName: DeviceName) =>
    `/${deviceName}/spotcam/capture_image_data_acquisition` as const,
  SPOT_CAM_SET_CAMERA: (deviceName: DeviceName) => `/${deviceName}/spotcam/set_camera` as const,
  SPOT_CAM_SET_LIGHTING: (deviceName: DeviceName) => `/${deviceName}/spotcam/set_lighting` as const,
  SPOT_CAM_PTZ_ADJUST_POSITION: (deviceName: DeviceName) =>
    `/${deviceName}/spotcam/ptz/adjust_position` as const,
  SPOT_CAM_PTZ_SET_POSITION: (deviceName: DeviceName) =>
    `/${deviceName}/spotcam/ptz/set_position` as const,
  REQUEST_UPLOAD_ALL_PENDING: (deviceName: DeviceName) =>
    `/${deviceName}/acquired_data_uploader/request_upload_all_pending` as const,
  NETWORK_ANALYSIS: (deviceName: DeviceName) => `/${deviceName}/network_analysis` as const,
  E_STOP_RELEASE: (deviceName: DeviceName) => `/${deviceName}/estop_release` as const,
  E_STOP_TRIGGER: (deviceName: DeviceName) => `/${deviceName}/estop_trigger` as const,
  CLAIM: (deviceName: DeviceName) => `/${deviceName}/claim` as const,
  POWER_ON: (deviceName: DeviceName) => `/${deviceName}/power_on` as const,
  POWER_OFF: (deviceName: DeviceName) => `/${deviceName}/power_off` as const,
  UNDOCK: (deviceName: DeviceName) => `/${deviceName}/undock` as const,
  DOCK: (deviceName: DeviceName) => `/${deviceName}/dock` as const,
  STAND: (deviceName: DeviceName) => `/${deviceName}/stand` as const,
  SIT: (deviceName: DeviceName) => `/${deviceName}/sit` as const,
  SELF_RIGHT: (deviceName: DeviceName) => `/${deviceName}/self_right` as const,
  ROLL_OVER: (deviceName: DeviceName) => `/${deviceName}/rollover` as const,
  LOCOMOTION_MODE: (deviceName: DeviceName) => `/${deviceName}/locomotion_mode` as const,
  STAIR_MODE: (deviceName: DeviceName) => `/${deviceName}/stair_mode` as const,
  CANCEL_ACTION: (deviceName: DeviceName) => `/${deviceName}/cancel_action` as const,
  ARM_LOCK_BODY: (deviceName: DeviceName) => `/${deviceName}/arm_lock_body` as const,
  ARM_LOCK_WORLD: (deviceName: DeviceName) => `/${deviceName}/arm_lock_world` as const,
  // ARM_STOW: (deviceName: DeviceName) => `/${deviceName}/arm_stow` as const,
  // ARM_UNSTOW: (deviceName: DeviceName) => `/${deviceName}/arm_unstow` as const,

  // - Drone-specific services (DJI PSDK ROS2)
  // TODO - verify if these are correct (99% sure they're not)
  DRONE_DEBUG_MODE_OPEN: (deviceName: DeviceName) => `/${deviceName}/debug_mode_open` as const,
  DRONE_DEBUG_MODE_CLOSE: (deviceName: DeviceName) => `/${deviceName}/debug_mode_close` as const,
  DRONE_GRAB_PAYLOAD_CTRL_AUTHORITY: (deviceName: DeviceName) =>
    `/${deviceName}/payload_control_authority_grab` as const,
  DRONE_GRAB_FLIGHT_CTRL_AUTHORITY: (deviceName: DeviceName) =>
    `/${deviceName}/grabbing_flight_control_authority` as const,
  DRONE_ENTER_LIVE_FLIGHT_CTR_MODE: (deviceName: DeviceName) =>
    `/${deviceName}/enter_live_flight_controls_mode` as const,
  DRONE_EXIT_LIVE_FLIGHT_CTR_MODE: (deviceName: DeviceName) =>
    `/${deviceName}/exit_live_flight_control_authority` as const,
  DRONE_TAKEOFF: (deviceName: DeviceName) => `/${deviceName}/one_key_takeoff` as const,
  DRONE_EMERGENCY_LAND: (deviceName: DeviceName) => `/${deviceName}/emergency_landing` as const,
  DRONE_FORCE_LANDING: (deviceName: DeviceName) => `/${deviceName}/forced_landing` as const,
  DRONE_RETURN_TO_HOME: (deviceName: DeviceName) => `/${deviceName}/one_key_return` as const,
  DRONE_SUPPLEMENT_LIGHT_OPEN: (deviceName: DeviceName) =>
    `/${deviceName}/supplement_light_open` as const,
  DRONE_SUPPLEMENT_LIGHT_CLOSE: (deviceName: DeviceName) =>
    `/${deviceName}/supplement_light_close` as const,
  DRONE_DOCK_COVER_OPEN: (deviceName: DeviceName) => `/${deviceName}/cover_open` as const,
  DRONE_DOCK_COVER_CLOSE: (deviceName: DeviceName) => `/${deviceName}/cover_close` as const,
  DRONE_OPEN: (deviceName: DeviceName) => `/${deviceName}/drone_open` as const,
  DRONE_CLOSE: (deviceName: DeviceName) => `/${deviceName}/drone_close` as const,
  // - - Gimbal services
  SET_GIMBLE_LENSE: (deviceName: DeviceName) => `/${deviceName}/set_camera` as const,
  PAYLOAD_CONTROL_SWITCH_CAMERA_MODE: (deviceName: DeviceName) =>
    `/${deviceName}/payload_control_switch_camera_mode` as const,
  PAYLOAD_CONTROL_ZOOM: (deviceName: DeviceName) => `/${deviceName}/payload_control_zoom` as const,
  PAYLOAD_CONTROL_RESET_GIMBAL: (deviceName: DeviceName) =>
    `/${deviceName}/payload_control_reset_gimbal` as const,
  SET_PROPERTY: (deviceName: DeviceName) => `/${deviceName}/set_property` as const,
  DOWNLOAD_MAP: (deviceName: DeviceName) => `/${deviceName}/download_map` as const,
  LAND: (deviceName: DeviceName) => `/${deviceName}/land` as const,
  SET_ARMED: (deviceName: DeviceName) => `/${deviceName}/set_armed` as const,
} as const;

export enum PAYLOAD_CONTROL_SWITCH_CAMERA_MODE {
  Capturing = 0,
  Recording = 1,
  SmartLowLight = 2,
  Panorama = 3,
  TimeShot = 4,
}

//(0=Reset, 1=Downward, 2=Recenter, 3=Pitch down)
export enum PAYLOAD_CONTROL_RESET_MODE {
  Reset = 0,
  Downward = 1,
  GimbalPanRecenter = 2,
  GimbalPitchDownward = 3,
}

export type DeviceNameServices = ReturnType<ValueOf<typeof DEVICE_NAME_SERVICES>>;

/** Structural type for any ROS service path of the form `/${deviceName}/...` */
export type DeviceServicePath = `/${string}/${string}`;

// todo - no need for this
export const ROS_SERVICES = {
  ROS_API,
} as const;

// helper for getting double nested objects values types
type DistributiveValues<T extends Record<string, any>> = T extends T ? T[keyof T] : never;
type InnerValues<T extends Record<keyof T, object>, K extends keyof T> = DistributiveValues<T[K]>;

export type RosServiceName = InnerValues<typeof ROS_SERVICES, keyof typeof ROS_SERVICES>; // is number|string

class RosServiceHandler {
  private readonly ros: RosSignalR;
  private readonly deviceName: DeviceName;

  constructor(ros: RosSignalR, deviceName: DeviceName) {
    this.ros = ros;
    this.deviceName = deviceName;
  }

  async LocalizeToMap(map_id: number) {
    const name = DEVICE_NAME_SERVICES.LOCALIZE_TO_MAP(this.deviceName);
    const LEASE_BOSDYN_SDK_CLIENT = 'bosdyn_sdk_client';
    const LEASE_ROS2_SPOT_WRAPPER_CLIENT = 'ros2_spot_wrapper_client';
    return this.ros.callRosService<
      spot_localization_handler_interfaces.srv.LocalizeToMap_Request,
      spot_localization_handler_interfaces.srv.LocalizeToMap_Response
    >(name, {
      map_id,
      loop_closure: true,
      optimize_anchoring: true,
      undock_to_localize: false,
      lease_level: LEASE_BOSDYN_SDK_CLIENT,
    });
  }

  async StartMapRecording(map_name: string) {
    const name = DEVICE_NAME_SERVICES.START_MAP_RECORDING(this.deviceName);
    return this.ros.callRosService<
      localization_handler_interfaces.srv.MapRecord_Request,
      localization_handler_interfaces.srv.MapRecord_Response
    >(name, { map_name });
  }

  async StopMapRecording() {
    const name = DEVICE_NAME_SERVICES.STOP_MAP_RECORDING(this.deviceName);
    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name, {});
  }

  async CancelMapRecording() {
    const name = DEVICE_NAME_SERVICES.CANCEL_MAP_RECORDING(this.deviceName);
    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name, {});
  }

  async NCBAvailableServices() {
    const name = DEVICE_NAME_SERVICES.NCB_AVAILABLE_SERVICES(this.deviceName);
    return this.ros.callRosService<
      spot_ncb_rms_actions_interfaces.srv.AvailableNcb_Request,
      spot_ncb_rms_actions_interfaces.srv.AvailableNcb_Response
    >(name, {});
  }

  async RMSAvailableServices() {
    const name = DEVICE_NAME_SERVICES.RMS_AVAILABLE_SERVICES(this.deviceName);
    return this.ros.callRosService<
      spot_ncb_rms_actions_interfaces.srv.AvailableRms_Request,
      spot_ncb_rms_actions_interfaces.srv.AvailableRms_Response
    >(name, {});
  }

  async NCBCallService(request: string) {
    const name = DEVICE_NAME_SERVICES.NCB_CALL_SERVICE(this.deviceName);
    return this.ros.callRosService<
      spot_ncb_rms_actions_interfaces.srv.NetworkCompute_Request,
      spot_ncb_rms_actions_interfaces.srv.NetworkCompute_Response
    >(name, { request });
  }

  async requestStreamChange(payload: RequestStreamChangePayload) {
    const name = DEVICE_NAME_SERVICES.REQUEST_STREAM_CHANGE(this.deviceName);

    return this.ros.callRosService<
      webrtc_streaming_interfaces.srv.StreamChange_Request,
      webrtc_streaming_interfaces.srv.StreamChange_Response
    >(name, { json_request: JSON.stringify(payload) });
  }

  async playSoundFromBlob(blob_name: string) {
    const name = DEVICE_NAME_SERVICES.PLAY_SOUND_FROM_BLOB(this.deviceName);

    return this.ros.callRosService<
      spot_spotcam_ptz_interfaces.srv.PlaySoundFromBlob_Request,
      spot_spotcam_ptz_interfaces.srv.PlaySoundFromBlob_Response
    >(name, { blob_name });
  }

  async clearBehaviorFault(id: number) {
    const name = DEVICE_NAME_SERVICES.CLEAR_BEHAVIOR_FAULT(this.deviceName);

    return this.ros.callRosService<
      spot_msgs.srv.ClearBehaviorFault_Request,
      spot_msgs.srv.ClearBehaviorFault_Response
    >(name, { id });
  }

  async setFlukeStreamFrequencyRange(
    stream_range: fluke_sensor_interfaces.msg.FlukeStreamFrequencyRange,
  ) {
    const name = DEVICE_NAME_SERVICES.SET_FLUKE_STREAM_FREQUENCY_RANGE(this.deviceName);

    return this.ros.callRosService<
      fluke_sensor_interfaces.srv.SetFlukeStreamFrequencyRange_Request,
      fluke_sensor_interfaces.srv.SetFlukeStreamFrequencyRange_Response
    >(name, { stream_range });
  }

  async captureStoreFlukeImage() {
    const name = DEVICE_NAME_SERVICES.CAPTURE_STORE_FLUKE_IMAGE(this.deviceName);

    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name);
  }

  async release() {
    const name = DEVICE_NAME_SERVICES.RELEASE(this.deviceName);

    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name);
  }

  async blkArcCaptureStart() {
    const name = DEVICE_NAME_SERVICES.BLK_ARC_START_CAPTURE(this.deviceName);

    return this.ros.callRosService<
      blk_arc_interfaces.srv.Capture_Request,
      blk_arc_interfaces.srv.Capture_Response
    >(name, { ignore_no_cascade_map: false, ignore_spot_localization: false });
  }

  async blkArcCaptureCancel() {
    const name = DEVICE_NAME_SERVICES.BLK_ARC_CANCEL_CAPTURE(this.deviceName);

    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name, {});
  }

  async restartRobot() {
    const name = DEVICE_NAME_SERVICES.RESTART_ROBOT(this.deviceName);

    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name, {});
  }

  async spotCamPlaySound(sound_id: string) {
    const name = DEVICE_NAME_SERVICES.SPOT_CAM_PLAY_SOUND(this.deviceName);

    return this.ros.callRosService<
      spot_spotcam_ptz_interfaces.srv.PlaySoundDirectMethod_Request,
      spot_spotcam_ptz_interfaces.srv.PlaySoundDirectMethod_Response
    >(name, { sound_id });
  }

  async spotComSetSurroundView(showSurroundView: boolean) {
    const name = DEVICE_NAME_SERVICES.SPOT_CAM_SET_SURROUND_VIEW(this.deviceName);

    return this.ros.callRosService<std_srvs.srv.SetBool_Request, std_srvs.srv.SetBool_Response>(
      name,
      { data: showSurroundView },
    );
  }

  async spotCamPointFromPanoClick(
    click_position_relative_x: number,
    click_position_relative_y: number,
  ) {
    const name = DEVICE_NAME_SERVICES.SPOT_CAM_POINT_FROM_PANO_CLICK(this.deviceName);

    return this.ros.callRosService<
      spot_spotcam_ptz_interfaces.srv.PointFromPanoClick_Request,
      spot_spotcam_ptz_interfaces.srv.PointFromPanoClick_Response
    >(name, { click_position_relative_x, click_position_relative_y });
  }

  async spotCamRecenter() {
    const name = DEVICE_NAME_SERVICES.SPOT_CAM_PTZ_RECENTER(this.deviceName);

    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name, {});
  }

  // todo - ask theo if we should do both?
  async spotCamCaptureImage(spotcam_image_source: 'ptz' | 'pano' = 'ptz') {
    const name = DEVICE_NAME_SERVICES.SPOT_CAM_CAPTURE_IMAGE(this.deviceName);

    return this.ros.callRosService<
      spot_spotcam_ptz_interfaces.srv.CaptureImageDataAcquisition_Request,
      spot_spotcam_ptz_interfaces.srv.CaptureImageDataAcquisition_Response
    >(name, { spotcam_image_source });
  }

  async spotCamSetCamera(camera: string, always_treat_as_compositor_string: boolean = false) {
    const name = DEVICE_NAME_SERVICES.SPOT_CAM_SET_CAMERA(this.deviceName);

    return this.ros.callRosService<
      spot_spotcam_ptz_interfaces.srv.SetCamera_Request,
      spot_spotcam_ptz_interfaces.srv.SetCamera_Response
    >(name, { camera, always_treat_as_compositor_string });
  }

  /**
   * There is also a get_lightning witch we could use for scale this
   * @param light_intensity
   */
  async spotCamSetLightning(light_intensity: number) {
    const name = DEVICE_NAME_SERVICES.SPOT_CAM_SET_LIGHTING(this.deviceName);

    return this.ros.callRosService<
      spot_spotcam_ptz_interfaces.srv.SetLighting_Request,
      spot_spotcam_ptz_interfaces.srv.SetLighting_Response
    >(name, { light_intensity });
  }

  async spotCamPtzSetPosition(pan: number, tilt: number, zoom: number) {
    const name = DEVICE_NAME_SERVICES.SPOT_CAM_PTZ_SET_POSITION(this.deviceName);

    const position = { pan, tilt, zoom };
    return this.ros.callRosService<
      spot_spotcam_ptz_interfaces.srv.PtzSetPosition_Request,
      spot_spotcam_ptz_interfaces.srv.PtzSetPosition_Response
    >(name, { position });
  }

  async spotCamPtzAdjustPosition(pan: number, tilt: number, zoom: number) {
    const name = DEVICE_NAME_SERVICES.SPOT_CAM_PTZ_ADJUST_POSITION(this.deviceName);
    const position_delta = {
      pan,
      tilt,
      zoom,
    };
    return this.ros.callRosService<
      spot_spotcam_ptz_interfaces.srv.PtzAdjustPosition_Request,
      spot_spotcam_ptz_interfaces.srv.PtzAdjustPosition_Response
    >(name, {
      position_delta,
    });
  }

  // todo - not tested nor suported by omar
  async _payloadControlSwitchCameraMode(
    payload_index: string,
    camera_mode: PAYLOAD_CONTROL_SWITCH_CAMERA_MODE,
  ) {
    const name = DEVICE_NAME_SERVICES.PAYLOAD_CONTROL_SWITCH_CAMERA_MODE(this.deviceName);
    return this.ros.callRosService<
      opg_msgs.srv.PayloadControlSwitchCameraMode_Request,
      opg_msgs.srv.PayloadControlSwitchCameraMode_Response
    >(name, { payload_index, camera_mode });
  }

  async setGimbleLense(video_id: string, video_type: 'ir' | 'wide' | 'zoom') {
    const name = DEVICE_NAME_SERVICES.SET_GIMBLE_LENSE(this.deviceName);
    return this.ros.callRosService<
      opg_msgs.srv.SetGimbalLens_Request,
      opg_msgs.srv.SetGimbalLens_Response
    >(name, { video_id, video_type });
  }

  // { wide: 'Wide angle', zoom: 'Zoom' };
  // zoom {"max":200,"min":2}
  async payloadControlZoom(
    payload_index: string,
    camera_type: 'zoom' | 'wide',
    zoom_factor: number,
  ) {
    const name = DEVICE_NAME_SERVICES.PAYLOAD_CONTROL_ZOOM(this.deviceName);
    return this.ros.callRosService<
      opg_msgs.srv.PayloadControlZoom_Request,
      opg_msgs.srv.PayloadControlZoom_Response
    >(name, { payload_index, camera_type, zoom_factor });
  }

  // {"0":"Reset","1":"Downward","2":"Gimbal pan recenter","3":"Gimbal pitch downward"}
  async payloadControlResetGimbal(payload_index: string, reset_mode: PAYLOAD_CONTROL_RESET_MODE) {
    const name = DEVICE_NAME_SERVICES.PAYLOAD_CONTROL_RESET_GIMBAL(this.deviceName);
    return this.ros.callRosService<
      opg_msgs.srv.PayloadControlResetGimbal_Request,
      opg_msgs.srv.PayloadControlResetGimbal_Response
    >(name, { payload_index, reset_mode });
  }

  async requestUploadAllPending() {
    const name = DEVICE_NAME_SERVICES.REQUEST_UPLOAD_ALL_PENDING(this.deviceName);

    return this.ros.callRosService<
      data_acquisition_interfaces.srv.AcquiredDataUploadRequestUploadAll_Request,
      data_acquisition_interfaces.srv.AcquiredDataUploadRequestUploadAll_Response
    >(name, {});
  }

  async networkAnalyse() {
    const name = DEVICE_NAME_SERVICES.NETWORK_ANALYSIS(this.deviceName);

    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name, {});
  }

  async eStopRelease() {
    const name = DEVICE_NAME_SERVICES.E_STOP_RELEASE(this.deviceName);

    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name, {});
  }

  async eStopTrigger() {
    const name = DEVICE_NAME_SERVICES.E_STOP_TRIGGER(this.deviceName);

    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name, {});
  }

  async claim() {
    const name = DEVICE_NAME_SERVICES.CLAIM(this.deviceName);

    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name, {});
  }

  async powerOn() {
    const name = DEVICE_NAME_SERVICES.POWER_ON(this.deviceName);

    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name, {});
  }

  async powerOff() {
    const name = DEVICE_NAME_SERVICES.POWER_OFF(this.deviceName);

    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name, {});
  }

  async dock() {
    const name = DEVICE_NAME_SERVICES.DOCK(this.deviceName);
    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name, {});
  }

  async undock() {
    const name = DEVICE_NAME_SERVICES.UNDOCK(this.deviceName);
    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name, {});
  }

  async sit() {
    const name = DEVICE_NAME_SERVICES.SIT(this.deviceName);
    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name, {});
  }

  async stand() {
    const name = DEVICE_NAME_SERVICES.STAND(this.deviceName);
    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name, {});
  }

  async selfRight() {
    const name = DEVICE_NAME_SERVICES.SELF_RIGHT(this.deviceName);
    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name, {});
  }

  /**
   * for battery changing
   */
  async rollOver() {
    const name = DEVICE_NAME_SERVICES.ROLL_OVER(this.deviceName);
    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name, {});
  }

  /**
   *
   * @param crawl sending false bring it back to normal mode
   */
  async locomotionMode(crawl: boolean) {
    const locomotion_mode = crawl
      ? IRosTypeBosdynSpotApiMsgsLocomotionHintConst.HINT_CRAWL
      : IRosTypeBosdynSpotApiMsgsLocomotionHintConst.HINT_AUTO;

    const name = DEVICE_NAME_SERVICES.LOCOMOTION_MODE(this.deviceName);
    return this.ros.callRosService<
      spot_msgs.srv.SetLocomotion_Request,
      spot_msgs.srv.SetLocomotion_Response
    >(name, { locomotion_mode });
  }

  /**
   * @param on - true is "on" and false is "auto"
   */
  async stairMode(on: boolean = true) {
    const name = DEVICE_NAME_SERVICES.STAIR_MODE(this.deviceName);
    return this.ros.callRosService<std_srvs.srv.SetBool_Request, std_srvs.srv.SetBool_Response>(
      name,
      { data: on },
    );
  }

  async cancelAction(action_name: RosActionName) {
    const name = DEVICE_NAME_SERVICES.CANCEL_ACTION(this.deviceName);
    return this.ros.callRosService<
      signalr_client_interfaces.srv.CancelAction_Request,
      signalr_client_interfaces.srv.CancelAction_Response
    >(name, { action_name: action_name.split('/')[2] });
  }

  async sendRobotCommand(command: bosdyn_api_msgs.msg.RobotCommand) {
    const name = DEVICE_NAME_SERVICES.ROBOT_COMMAND(this.deviceName);
    return this.ros.callRosService<
      spot_msgs.srv.RobotCommand_Request,
      spot_msgs.srv.RobotCommand_Response
      // @ts-expect-error - TODO - fix this
    >(name, command);
  }

  async armLockBody() {
    const name = DEVICE_NAME_SERVICES.ARM_LOCK_BODY(this.deviceName);
    return this.ros.callRosService<std_srvs.srv.Trigger_Request, std_srvs.srv.Trigger_Response>(
      name,
    );
  }

  async armLockWorld() {
    const name = DEVICE_NAME_SERVICES.ARM_LOCK_WORLD(this.deviceName);
    return this.ros.callRosService<std_srvs.srv.Trigger_Request, std_srvs.srv.Trigger_Response>(
      name,
    );
  }

  // async armStow() {
  //   const name = DEVICE_NAME_SERVICES.ARM_STOW(this.deviceName);
  //   return this.ros.callRosService<std_srvs.srv.Trigger_Request, std_srvs.srv.Trigger_Response>(
  //     name,
  //   );
  // }
  //
  // async armUnStow() {
  //   const name = DEVICE_NAME_SERVICES.ARM_UNSTOW(this.deviceName);
  //   return this.ros.callRosService<std_srvs.srv.Trigger_Request, std_srvs.srv.Trigger_Response>(
  //     name,
  //   );
  // }

  // - - - - - - - - Drone-specific service methods (DJI PSDK ROS2) - - - - - - - -

  async droneDebugModeOpen(): Promise<Trigger_Response> {
    const name = DEVICE_NAME_SERVICES.DRONE_DEBUG_MODE_OPEN(this.deviceName);
    console.log(`Calling ${name}`);
    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name, {});
  }

  async droneDebugModeClose(): Promise<Trigger_Response> {
    const name = DEVICE_NAME_SERVICES.DRONE_DEBUG_MODE_CLOSE(this.deviceName);
    console.log(`Calling ${name}`);
    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name, {});
  }

  async droneGrabFlightControlAuthority(): Promise<Trigger_Response> {
    // this has replaced the "motor on"
    const name = DEVICE_NAME_SERVICES.DRONE_GRAB_FLIGHT_CTRL_AUTHORITY(this.deviceName);
    console.log(`Calling ${name}`);

    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name, {});
  }

  async droneGrabPayloadControlAuthority(payload_index: string): Promise<Trigger_Response> {
    const name = DEVICE_NAME_SERVICES.DRONE_GRAB_PAYLOAD_CTRL_AUTHORITY(this.deviceName);
    console.log(`Calling ${name}`);

    return this.ros.callRosService<
      opg_msgs.srv.PayloadControlAuthorityGrab_Request,
      opg_msgs.srv.PayloadControlAuthorityGrab_Response
    >(name, { payload_index });
  }

  async droneEnterLiveFlightControlMode(
    request: opg_msgs.srv.EnterLiveFlightControlsMode_Request,
  ): Promise<Trigger_Response> {
    // this has replaced the "motor on"
    const name = DEVICE_NAME_SERVICES.DRONE_ENTER_LIVE_FLIGHT_CTR_MODE(this.deviceName);
    console.log(`Calling ${name}`);

    return this.ros.callRosService<
      opg_msgs.srv.EnterLiveFlightControlsMode_Request,
      opg_msgs.srv.EnterLiveFlightControlsMode_Response
    >(name, request);
  }

  async droneExitLiveFlightControlMode(): Promise<Trigger_Response> {
    // this has replaced the "motor off"
    const name = DEVICE_NAME_SERVICES.DRONE_EXIT_LIVE_FLIGHT_CTR_MODE(this.deviceName);
    console.log(`Calling ${name}`);

    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name, {});
  }

  async droneTakeoff(request: opg_msgs.srv.OneKeyTakeoff_Request): Promise<Trigger_Response> {
    const name = DEVICE_NAME_SERVICES.DRONE_TAKEOFF(this.deviceName);

    return this.ros.callRosService<
      opg_msgs.srv.OneKeyTakeoff_Request,
      opg_msgs.srv.OneKeyTakeoff_Response
    >(name, request);
  }

  async droneEmergencyLand(): Promise<Trigger_Response> {
    const name = DEVICE_NAME_SERVICES.DRONE_EMERGENCY_LAND(this.deviceName);
    console.log(`Calling ${name}`);

    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name, {});
  }

  async droneForceLanding(): Promise<Trigger_Response> {
    const name = DEVICE_NAME_SERVICES.DRONE_FORCE_LANDING(this.deviceName);
    console.log(`Calling ${name}`);

    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name, {});
  }

  //TODO: change the response
  async droneReturnToHome(): Promise<Trigger_Response> {
    const name = DEVICE_NAME_SERVICES.DRONE_RETURN_TO_HOME(this.deviceName);
    console.log(`Calling ${name}`);

    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name, {});
  }

  // - - Drone: In-debug mode section

  async supplementLightOpen() {
    const name = DEVICE_NAME_SERVICES.DRONE_SUPPLEMENT_LIGHT_OPEN(this.deviceName);
    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name, {});
  }

  async supplementLightClose() {
    const name = DEVICE_NAME_SERVICES.DRONE_SUPPLEMENT_LIGHT_CLOSE(this.deviceName);
    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name, {});
  }

  async dockCoverOpen() {
    const name = DEVICE_NAME_SERVICES.DRONE_DOCK_COVER_OPEN(this.deviceName);
    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name, {});
  }

  async dockCoverClose() {
    const name = DEVICE_NAME_SERVICES.DRONE_DOCK_COVER_CLOSE(this.deviceName);
    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name, {});
  }

  async droneOpen() {
    const name = DEVICE_NAME_SERVICES.DRONE_OPEN(this.deviceName);
    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name, {});
  }
  async droneClose() {
    const name = DEVICE_NAME_SERVICES.DRONE_CLOSE(this.deviceName);
    return this.ros.callRosService<Trigger_Request, Trigger_Response>(name, {});
  }

  async setProperty(property_name: string, property_value: string) {
    const name = DEVICE_NAME_SERVICES.SET_PROPERTY(this.deviceName);
    return this.ros.callRosService<
      opg_msgs.srv.SetProperty_Request,
      opg_msgs.srv.SetProperty_Response
    >(name, { property_name, property_value });
  }

  async downloadMap(mapId: string) {
    const name = DEVICE_NAME_SERVICES.DOWNLOAD_MAP(this.deviceName);

    return this.ros.callRosService<
      localization_handler_interfaces.srv.DownloadMap_Request,
      localization_handler_interfaces.srv.DownloadMap_Response
    >(name, {
      map_id: mapId,
      unzip_path: '',
    });
  }

  async land(emergency: boolean) {
    const name = DEVICE_NAME_SERVICES.LAND(this.deviceName);
    return this.ros.callRosService<
      aerial_interfaces_srv.Land_Request,
      aerial_interfaces_srv.Land_Response
    >(name, { emergency });
  }

  async setArmed(armed: boolean) {
    const name = DEVICE_NAME_SERVICES.SET_ARMED(this.deviceName);
    return this.ros.callRosService<
      robot_interfaces_srv.SetArmed_Request,
      robot_interfaces_srv.SetArmed_Response
    >(name, { armed });
  }
}

export function useRosService() {
  const { ros } = useRosBridge();
  const { deviceName } = useDeviceName();

  const RosServices = useMemo(() => new RosServiceHandler(ros, deviceName), [deviceName]);

  return { RosServices };
}
